import { effect, inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { io, Socket } from 'socket.io-client';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastsService } from '@catch/services';
import { selectToken, selectTokenPayload } from '../auth/auth.reducer';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { AppState } from '../../app.reducer';
import {
  exportFailed,
  exportReady,
  loadExportDetails,
  pruneExpiredExports,
  syncPendingExports,
  requestExport,
  requestExportFailure,
  requestExportSuccess,
  updateExportCreationTime,
} from './export-downloads.actions';
import { selectPendingExports, selectReadyExports } from './export-downloads.reducer';
import { ExportDownloadsEvent } from '../../enums/export-downloads.enum';

@Injectable()
export class ExportDownloadsEffects {
  private socket: Socket | null = null;
  private store = inject(Store<AppState>);
  private actions$ = inject(Actions);
  private jobsAdapter = inject(JobsAdapter);
  private toastService = inject(ToastsService);
  private jwtPayload = this.store.selectSignal(selectTokenPayload);
  private token = this.store.selectSignal(selectToken);

  payloadEffect = effect(() => {
    const payload = this.jwtPayload();
    if (payload) {
      this.store.dispatch(pruneExpiredExports({}));
      this.store.dispatch(syncPendingExports());
      this.initSocket();
    }
  });

  /** Backfill creationTime for persisted exports without it when user logs in */
  backfillCreationTimeOnLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(pruneExpiredExports),
        withLatestFrom(this.store.select(selectReadyExports)),
        map(([, exports]) => exports.filter((e) => !e.creationTime)),
        filter((withoutCreatedAt) => withoutCreatedAt.length > 0),
        map((exports) => loadExportDetails({ exports }))
      ),
    { dispatch: true }
  );

  private initSocket() {
    if (this.socket) {
      return;
    }

    const payload = this.jwtPayload();
    const token = this.token();
    if (!payload || !token) {
      return;
    }

    this.socket = io(`${environment.apiBaseUrl}/socket/export-downloads`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: {
        companyId: payload.companyId,
        userId: payload.userId,
        token,
      },
    });

    this.socket.on('connect', () => {
      this.store.dispatch(syncPendingExports());
    });

    this.socket.on(
      ExportDownloadsEvent.EXPORT_DOWNLOAD_READY,
      (data: { exportId: number; jobId: number; filename: string; downloadUrl: string; creationTime?: string }) => {
        this.store.dispatch(
          exportReady({
            exportId: data.exportId,
            jobId: data.jobId,
            filename: data.filename,
            downloadUrl: data.downloadUrl,
            creationTime: data.creationTime,
          })
        );
      }
    );

    this.socket.on(
      ExportDownloadsEvent.EXPORT_DOWNLOAD_FAILED,
      (data: { exportId: number; jobId: number; error?: string }) => {
        this.store.dispatch(
          exportFailed({
            exportId: data.exportId,
            jobId: data.jobId,
            error: data.error,
          })
        );
        this.toastService.alert(
          'company-tool.views.jobs.applications-list.actions.toasts.export-error'
        );
      }
    );
  }

  requestExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestExport),
      mergeMap((action) =>
        this.jobsAdapter
          .requestApplicationDocumentsExport(action.jobId, action.applicationIds)
          .pipe(
            map((response) =>
              requestExportSuccess({
                exportId: response.exportId,
                jobId: action.jobId,
                jobTitle: action.jobTitle,
              })
            ),
            catchError((error) => {
              this.toastService.alert(
                'company-tool.views.jobs.applications-list.actions.toasts.export-error'
              );
              return of(requestExportFailure({ error }));
            })
          )
      )
    )
  );

  syncPendingExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncPendingExports),
      withLatestFrom(this.store.select(selectPendingExports)),
      map(([, pending]) => pending),
      filter((pending) => pending.length > 0),
      mergeMap((pending) =>
        from(pending).pipe(
          mergeMap((exportItem) =>
            this.jobsAdapter
              .getExportStatus(exportItem.jobId, exportItem.exportId)
              .pipe(
                map((status) => {
                  if (
                    status.status === 'READY' &&
                    status.downloadUrl &&
                    status.filename
                  ) {
                    return exportReady({
                      exportId: exportItem.exportId,
                      jobId: exportItem.jobId,
                      filename: status.filename,
                      downloadUrl: status.downloadUrl,
                      creationTime: status.creationTime,
                    });
                  }

                  if (status.status === 'FAILED') {
                    return exportFailed({
                      exportId: exportItem.exportId,
                      jobId: exportItem.jobId,
                    });
                  }

                  return null;
                }),
                filter((action): action is ReturnType<typeof exportReady> | ReturnType<typeof exportFailed> => action !== null),
                catchError(() => EMPTY)
              )
          )
        )
      )
    )
  );

  loadExportDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadExportDetails),
      mergeMap(({ exports: exportsToLoad }) =>
        of(...exportsToLoad.filter((e) => !e.creationTime))
      ),
      mergeMap((exportItem) =>
        this.jobsAdapter.getExportStatus(exportItem.jobId, exportItem.exportId).pipe(
          map((status) =>
            status.creationTime
              ? updateExportCreationTime({
                  exportId: exportItem.exportId,
                  creationTime: status.creationTime,
                })
              : null
          ),
          filter((action): action is ReturnType<typeof updateExportCreationTime> =>
            action !== null
          ),
          catchError(() => of(null).pipe(filter(() => false)))
        )
      )
    )
  );
}
